<script setup lang="ts">
  import { useSlots } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';

  withDefaults(
    defineProps<{
      text: string;
      icon?: string;
      link?: { to: string; label: string };
      actionLabel?: string;
      /** Amber for "worth fixing", red for "nothing works until fixed". */
      tone?: 'warning' | 'danger';
      /** The action waits (a write is in flight). */
      disabled?: boolean;
    }>(),
    { icon: 'information-outline', tone: 'warning' }
  );
  const emit = defineEmits<{ (event: 'action'): void }>();
  const slots = useSlots();
</script>

<template>
  <div
    class="reservations-notice"
    :class="`tone-${tone}`"
    :role="tone === 'danger' ? 'alert' : 'status'"
  >
    <PosUiIcon :name="icon" :size="22" />
    <div class="notice-content">
      <p>{{ text }}</p>
      <router-link v-if="link" class="notice-link" :to="link.to">{{
        link.label
      }}</router-link>
      <div v-if="actionLabel || slots.actions" class="notice-actions">
        <PosUiButton
          v-if="actionLabel"
          color="neutral"
          variant="outline"
          :disabled="disabled"
          @click="emit('action')"
          >{{ actionLabel }}</PosUiButton
        >
        <slot name="actions" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .reservations-notice {
    @apply tw-mb-5 tw-flex tw-items-start tw-gap-3 tw-rounded-lg tw-border tw-border-solid tw-border-amber-200 tw-bg-amber-50 tw-p-4 tw-text-sm tw-text-amber-800;

    .notice-content {
      @apply tw-min-w-0;

      p {
        @apply tw-m-0 tw-leading-relaxed;
      }
    }

    .notice-link {
      @apply tw-inline-flex tw-min-h-11 tw-items-center tw-font-medium tw-text-amber-900 tw-underline;
    }

    .notice-actions {
      @apply tw-mt-3 tw-flex tw-flex-wrap tw-gap-2;
      // Table names are data: long labels wrap inside the notice.
      :deep(.pos-ui-button) {
        @apply tw-h-auto tw-min-h-11 tw-max-w-full tw-whitespace-normal tw-py-2 tw-text-left;
      }
    }

    &.tone-danger {
      @apply tw-border-red-200 tw-bg-red-50 tw-text-red-800;

      .notice-link {
        @apply tw-text-red-900;
      }
    }
  }
</style>
